import type { RefObject } from 'react';
import {
  useEffect,
  useRef,
  useState,
} from 'react';

import isNil from 'lodash/isNil';

function useFocusWithin<T extends HTMLElement>(): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [focusWithin, setFocusWithin] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (isNil(node)) {
      return;
    }

    const handleFocusIn = () => {
      setFocusWithin(true);
    };

    const handleFocusOut = (event: FocusEvent) => {
      const relatedTarget = event.relatedTarget;

      if (relatedTarget instanceof Node && node.contains(relatedTarget)) {
        return;
      }

      setFocusWithin(false);
    };

    node.addEventListener('focusin', handleFocusIn);
    node.addEventListener('focusout', handleFocusOut);

    return () => {
      node.removeEventListener('focusin', handleFocusIn);
      node.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  return [ref, focusWithin];
}

export default useFocusWithin;
